import React, { useEffect, useState, useCallback } from 'react';
import { loadHistoricalData, useHistoricalListings } from './services/historicalDataService';
import './styles.css';
import './styles/toggle-buttons.css';
import './styles/split-view.css';
import AppHeader from './components/AppHeader';
import ListingList from './ListingList';
import SplitView from './SplitView';
import HistoricalSplitView from './HistoricalSplitView';
import MapView from './MapView';
import TestMap from './TestMap';
import { fetchListings } from './api.js';
import agent from './agent.js';
import HistoricalListingsContainer from './components/HistoricalListings/HistoricalListingsContainer';
import BottomNavigation from './components/BottomNavigation';
import Footer from './components/Footer/Footer';
import { StyleConstants } from './StyleConstants';

const TABLET_MAX_WIDTH = 1190; // Devices up to this width will get the mobile header

function App() {

  const [listings, setListings] = useState([]);
  const { listings: historicalListings, error: historicalError } = useHistoricalListings();
  const [error, setError] = useState(null);
  const [currentListingsLoading, setCurrentListingsLoading] = useState(true);

  const getShouldRenderMobileLayout = useCallback(() => {
    return window.innerWidth <= TABLET_MAX_WIDTH;
  }, []);

  const [shouldRenderMobileLayout, setShouldRenderMobileLayout] = useState(getShouldRenderMobileLayout());
  const [viewMode, setViewMode] = useState('gallery');
  const [dataMode, setDataMode] = useState('current');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const initialDataMode = 'current';
  const initialFilterStatus = 'all';

  // Load current listings
  useEffect(() => {
    async function loadCurrentListings() {
      setCurrentListingsLoading(true);
      setError(null);
      try {
        const token = import.meta.env.VITE_SPARK_API_KEY;
        const agentId = agent.sparkAgentId;
        if (!token || !agentId) throw new Error('Missing Spark API key or agent ID');
        const data = await fetchListings({ agentId, token }); console.log("API Response - Total listings:", data.length); console.log("API Response - Statuses:", [...new Set(data.map(l => l.status))]);
        setListings(data);
      } catch (e) {
        setError(e.message);
        console.error('Fetch error:', e);
      } finally {
        setCurrentListingsLoading(false);
      }
    }
    loadCurrentListings();
  }, []);

  // Load historical listings - now handled by useHistoricalListings hook
  useEffect(() => {
    // Preload historical data when the app starts
    loadHistoricalData().catch(err => {
      console.error('Historical data preload error:', err);
    });
  }, []);

  // Update layout state on resize or orientation change
  useEffect(() => {
    const handleLayoutChange = () => {
      const newLayoutIsMobile = getShouldRenderMobileLayout();
      setShouldRenderMobileLayout(newLayoutIsMobile);
      if (newLayoutIsMobile && viewMode === 'split') {
        setViewMode('gallery');
      }
    };
    
    window.addEventListener('resize', handleLayoutChange);
    window.addEventListener('orientationchange', handleLayoutChange);
    handleLayoutChange(); // Initial check
    
    return () => {
      window.removeEventListener('resize', handleLayoutChange);
      window.removeEventListener('orientationchange', handleLayoutChange);
    };
  }, [viewMode, getShouldRenderMobileLayout]);

  const handleGoHome = useCallback(() => {
    const homeViewMode = getShouldRenderMobileLayout() ? 'gallery' : 'split';
    setViewMode(homeViewMode);
    setDataMode(initialDataMode);
    setFilterStatus(initialFilterStatus);
    console.log('Navigating home, resetting states.');
  }, [getShouldRenderMobileLayout]);

  // Effect to set .app height dynamically
  useEffect(() => {
    const appElement = document.querySelector('.app');
    const setAppHeight = () => {
      if (appElement) {
        if (shouldRenderMobileLayout) {
          appElement.style.height = `${window.innerHeight}px`;
        } else {
          appElement.style.height = '100%'; 
        }
      }
    };
    setAppHeight();
    window.addEventListener('resize', setAppHeight);
    window.addEventListener('orientationchange', setAppHeight);

    return () => {
      window.removeEventListener('resize', setAppHeight);
      window.removeEventListener('orientationchange', setAppHeight);
    };
  }, [shouldRenderMobileLayout]);

  const handleViewModeChange = (mode) => {
    if (shouldRenderMobileLayout && mode === 'split') return;
    setViewMode(mode);
  };

  const handleBottomNavNavigate = ({ data, view }) => {
    // Timing for view navigation
    console.time(`Navigation-To-${data}-${view}`);
    if (shouldRenderMobileLayout && view === 'split') return;
    setDataMode(data);
    setViewMode(view);
    
    setTimeout(() => {
      console.timeEnd(`Navigation-To-${data}-${view}`);
      if (dataMode === 'historical') {
        console.log('⏱️ Starting Historical view render');
        try {
          // Reset timer if it was already running
          console.timeEnd('Historical-View-Render');
        } catch {
          // Ignore error if timer wasn't started
        }
        console.time('Historical-View-Render');
      } else {
        console.time('Current-View-Render');
      }
    }, 0);
  };

  const renderContent = () => {
    const mapViewWrapperStyle = {
      flex: '1 1 auto',
      width: '100%',
      overflow: 'hidden',
      position: 'relative',
      boxSizing: 'border-box'
    };

    if (error || historicalError) {
      return <div className="error">{error || historicalError?.message || 'An error occurred'}</div>;
    }

    if (dataMode === 'current') {
      switch (viewMode) {
        case 'split':
          return <SplitView listings={listings} isMobile={shouldRenderMobileLayout} />; 
        case 'gallery':
          return (
            <>
              <div style={{
                flexGrow: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                backgroundColor: 'var(--grey)',
                padding: `${StyleConstants.contentTopPadding} ${StyleConstants.contentSidePadding} 20px ${StyleConstants.contentSidePadding}`,
                width: '100%',
                boxSizing: 'border-box',
              }}>
                <ListingList 
                  listings={listings} 
                  filterStatus={filterStatus}
                  onFilterChange={(e) => setFilterStatus(e.target.value)}
                  isLoading={currentListingsLoading}
                  isMobile={shouldRenderMobileLayout}
                />
              </div>
              {!shouldRenderMobileLayout && <Footer />}
            </>
          );
        case 'map':
          return (
            <div style={mapViewWrapperStyle}>
              <MapView listings={listings} isMobile={shouldRenderMobileLayout} />
            </div>
          );
        default:
          return <SplitView listings={listings} isMobile={shouldRenderMobileLayout} />;
      }
    } else { // historical mode
      if (viewMode === 'split' && !shouldRenderMobileLayout) {
        return <HistoricalSplitView listings={historicalListings} isMobile={shouldRenderMobileLayout} />; 
      }
      switch (viewMode) {
        case 'gallery':
        case 'split': // Fallback for historical split on mobile layout
          return (
            <>
              <div style={{
                flexGrow: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                backgroundColor: 'var(--grey)',
                padding: `0 ${StyleConstants.contentSidePadding} 20px ${StyleConstants.contentSidePadding}`,
                width: '100%',
                boxSizing: 'border-box',
              }}>
                <HistoricalListingsContainer listings={historicalListings} isSplitMode={viewMode === 'split' && !shouldRenderMobileLayout} />
              </div>
              {!shouldRenderMobileLayout && <Footer />}
            </>
          );
        case 'map':
          return (
            <div style={mapViewWrapperStyle}>
              <TestMap isMobile={shouldRenderMobileLayout} />
            </div>
          );
        default: // Fallback to gallery
          return (
             <>
              <div style={{
                flexGrow: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                backgroundColor: 'var(--grey)',
                padding: `0 ${StyleConstants.contentSidePadding} 20px ${StyleConstants.contentSidePadding}`,
                width: '100%',
                boxSizing: 'border-box',
              }}>
                <HistoricalListingsContainer listings={historicalListings} isSplitMode={false} />
              </div>
              {!shouldRenderMobileLayout && <Footer />}
            </>
          );
      }
    }
  };



  return (
    <div className="app" style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%', 
      width: '100%',
      overflow: 'hidden'
    }}>
      <div id="app-header-wrapper" style={{ flexShrink: 0, width: '100%' }}>
        <AppHeader
          agent={agent}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          dataMode={dataMode}
          onDataModeChange={setDataMode}
          isMobile={shouldRenderMobileLayout} // This prop name is kept for AppHeader's internal use
          onGoHome={handleGoHome}
        />
      </div>
      <main id="main-content-area" style={{ 
        flexGrow: 1, 
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {renderContent()}
      </main>
      {shouldRenderMobileLayout && (
        <div style={{ flexShrink: 0, width: '100%' }} id="mobile-bottom-nav-wrapper">
          <BottomNavigation
            activeView={{ dataMode, viewMode }}
            onNavigate={handleBottomNavNavigate}
            className="mobile-visible"
          />
        </div>
      )}
    </div>
  );
}

export default App;
